import Footer from '@/components/Footer';
import { Icons } from '@/components/icons';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import JourneyCard from '@/components/purchase/journey/JourneyCard';
import { TransText } from '@/i18n/trans/text';
import type {
  SearchJourneyOffersDTO,
  SearchOffersDTO,
} from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { getJourneyWarning, getWarningsByTripId } from '@/utils/warning';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

interface JourneyListProps extends SearchOffersDTO {
  setSelectedJourney: ActionCreatorWithPayload<SearchJourneyOffersDTO>;
  onContinue: () => void;
  onGoBack?: () => void;
  title: ReactNode;
}

const JourneyList: FC<JourneyListProps> = ({
  journeys,
  warnings,
  onContinue,
  onGoBack,
  setSelectedJourney,
  title,
}) => {
  const warningsByTripId = getWarningsByTripId(warnings?.warnings);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const location = useLocation();

  return (
    <>
      <div
        className={cn('mx-auto w-full max-w-laptop p-4', {
          ['pb-22']: location.pathname === '/purchase/inbound/journey',
        })}
      >
        <div className="mx-auto max-w-mobile" data-testid="journey-list">
          <Typography
            asChild
            variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
            className="mb-4"
          >
            <h1>{title}</h1>
          </Typography>
          <div className="grid grid-cols-[1fr_auto] gap-y-4">
            {journeys?.map((journey, key) => (
              <JourneyCard
                key={key}
                journey={journey}
                warningMessage={getJourneyWarning(journey, warningsByTripId)}
                onContinue={onContinue}
                setSelectedJourney={setSelectedJourney}
              />
            ))}
          </div>
        </div>
      </div>
      {onGoBack && (
        <Footer
          actionButtons={
            <>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                fullWidth
                data-testid="footer-back-button"
                className="rounded-lg laptop:w-auto"
                variant="tertiary"
                onClick={onGoBack}
              >
                <Icons.arrowLeft />
                <TransText i18nKey="back" />
              </Button>
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                fullWidth
                data-testid="footer-continue-button"
                className="rounded-lg laptop:w-auto"
                onClick={onContinue}
                variant="cta"
                disabled
              >
                <TransText i18nKey="continue" />
                <Icons.arrowRight />
              </Button>
            </>
          }
        />
      )}
    </>
  );
};

export default JourneyList;
